'use client';

import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { SearchResultTab } from './search-result-tab.component';

import { AllSearchResults, SearchType, SelectedProcedures } from '@/app/(main)/_components/search-modal/search-modal.types';
import { SECTION_RESULT_LIMIT } from '@/app/(main)/_components/search-modal/_consts/search-results.consts';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { setSelectedFilter } from '@/store/search/search.store';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { scrollSearchResultsToTop } from '@/utils/scroll-search-results-to-top.util';
import { SearchModalListEnum } from '@/types/enums/analytics/home/home-analytics.enum';
import { Button } from '@/components/ui/button/button.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { cn } from '@/utils/cn-tw-merge';

interface SearchSectionsProps {
  results: AllSearchResults;
  searchTerm: string;
  setOpen: (open: boolean) => void;
  selectLaboratoryProcedure?: (procedure: SelectedProcedures) => void;
  locationOverride?: OverridedUserLocation;
}

const SECTIONS = [
  { type: SearchType.PROCEDURE, label: 'Atendimentos' },
  { type: SearchType.PROFESSIONAL, label: 'Profissionais' },
  { type: SearchType.PARTNER, label: 'Locais' },
];

export function SearchSections({
  results,
  searchTerm,
  setOpen,
  selectLaboratoryProcedure,
  locationOverride,
}: SearchSectionsProps) {
  const handleSeeAll = (type: SearchType) => {
    setSelectedFilter(type);
    scrollSearchResultsToTop();
    getAnalyticsPlugin().track(AnalyticsEnum.CLICK_SEARCH_MODAL_SEE_ALL, {
      categoria: type.toLocaleLowerCase(),
    });
  };

  const visibleSections = SECTIONS
    .map((section) => ({ ...section, results: results[section.type as keyof AllSearchResults] }))
    .filter(({ results: section }) => section.results.length > 0);

  return (
    <div>
      {visibleSections.map(({ type, label, results: section }, sectionIndex) => {
        const isHighlighted = sectionIndex % 2 === 1;

        return (
          <section
            key={type}
            data-testid={DataTestIdEnum.PROCEDURES_MODAL_SEARCH_SECTION}
            className="pt-4"
          >
            <div className="flex items-center gap-6 px-6">
              <Typography className="flex-1 font-semibold text-gray-700">{label}</Typography>
              {section.hasMore && (
                <Button
                  data-testid={DataTestIdEnum.PROCEDURES_MODAL_SEARCH_SECTION_SEE_ALL}
                  variant="text"
                  color="primary"
                  className="w-auto px-4 py-1.5 text-sm"
                  onClick={() => handleSeeAll(type)}
                >
                  Ver todos
                </Button>
              )}
            </div>

            {section.results.slice(0, SECTION_RESULT_LIMIT).map((result, index) => (
              <div
                key={`${result.id}${result.displayName}-${index}`}
                className={cn('transition-colors', isHighlighted ? 'hover:bg-green-50' : 'hover:bg-gray-50')}
              >
                <SearchResultTab
                  {...result}
                  variant="compact"
                  avatarTone={isHighlighted ? 'highlight' : undefined}
                  locationOverride={locationOverride}
                  searchTerm={searchTerm}
                  list={SearchModalListEnum.RESULTS}
                  position={index + 1}
                  totalResults={section.results.length}
                  setOpen={setOpen}
                  selectLaboratoryProcedure={selectLaboratoryProcedure}
                />
                <hr className={cn(isHighlighted && 'border-success-300')} />
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}
